// (same imports as before)
import React, { useMemo, useState, useContext } from "react";
import { Box, Typography } from "@mui/material";
import { useAppConfig } from "../../../../context/AppConfigContext";
import { useWeddingWizard } from "../../../../context/WeddingWizardContext";
import { CartContext } from "../../../../context/CartContext";
import SharedCartPreview from "../../../Cart/SharedCartPreview";
import api from "../../../../services/api";

import { useValidation, buildPayload } from "./reviewUtils";
import { useReviewPreview } from "./useReviewPreview";

import ReviewValidationBanner from "./ReviewValidationBanner";
import ReviewServerWarnings from "./ReviewServerWarnings";
import ReviewEventDetails from "./ReviewEventDetails";
import ReviewCakeSummary from "./ReviewCakeSummary";
import ReviewDessertsSummary from "./ReviewDessertsSummary";
import ReviewRentalsSummary from "./ReviewRentalsSummary";
import ReviewLogisticsSummary from "./ReviewLogisticsSummary";
import ReviewPricePreview from "./ReviewPricePreview";
import ReviewCustomerSummary from "./ReviewCustomerSummary";
import PaymentPlanBox from "./PaymentPlanBox";

import {
  selectCakeTotal,
  selectDessertsTotal,
  selectRentalsTotal,
  selectDepositsTotal,
  selectDeliveryFee,
  selectSetupFee,
} from "./utils/reviewSelectors";

export default function ReviewStep() {
  const { config } = useAppConfig();
  const { state } = useWeddingWizard();
  const { addToCart } = useContext(CartContext);

  console.log(state);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [showCartPreview, setShowCartPreview] = useState(false);

  // v1 flags
  const hasCake = !!state.cake?.include;
  const hasDess = !!state.desserts?.include;
  const hasRentals =
    Array.isArray(state.rentals?.items) &&
    state.rentals.items.some((r) => Number(r?.days) > 0);
  const hasAnyProduct = hasCake || hasDess || hasRentals;

  // address/validity echo (for banner chips only)
  const addressConfirmed =
    Number(state.fulfillment?.address?.distanceKm || 0) > 0 &&
    !!state.fulfillment?.address?.line1;

  // Validation
  const clientIssues = useValidation(state);
  const canSubmit = hasAnyProduct && clientIssues.length === 0;

  // Preview deps — only re-run when relevant parts change
  const preview = useReviewPreview(state, {
    enabled: hasAnyProduct && clientIssues.length === 0,
    deps: [
      hasAnyProduct,
      // cake
      state.cake?.include,
      state.cake?.styleId,
      state.cake?.compositeKey,
      JSON.stringify(state.cake?.tiers || []),
      // desserts
      state.desserts?.include,
      JSON.stringify(state.desserts?.selections || []),
      // rentals
      JSON.stringify(state.rentals?.items || []),
      // fulfillment + customer (added)
      state.fulfillment?.method,
      state.fulfillment?.setupSelected,
      state.fulfillment?.scheduledDate,
      state.fulfillment?.address?.line1,
      state.fulfillment?.address?.city,
      state.fulfillment?.address?.province,
      state.fulfillment?.address?.postalCode,
      state.fulfillment?.address?.distanceKm,
      state.customer?.firstName,
      state.customer?.lastName,
      state.customer?.email,
    ],
  });

  const cakeSectionTotal = selectCakeTotal(preview);
  const dessertsSectionTotal = selectDessertsTotal(preview);
  const rentalsSectionTotal = selectRentalsTotal(preview);
  const depositsSectionTotal = selectDepositsTotal(preview);
  const deliveryFeeAmt = selectDeliveryFee(preview);
  const setupFeeAmt = selectSetupFee(preview);

  // Slice preview items by category for subcomponents
  const cakeLine = useMemo(
    () => (preview.items || []).find((i) => i.category === "weddingCake"),
    [preview.items]
  );
  const dessertLines = useMemo(
    () => (preview.items || []).filter((i) => i.category === "patisserie"),
    [preview.items]
  );
  const rentalLines = useMemo(
    () => (preview.items || []).filter((i) => i.category === "weddingRental"),
    [preview.items]
  );
  const depositLines = useMemo(
    () => (preview.items || []).filter((i) => i.category === "weddingDeposit"),
    [preview.items]
  );
  const deliveryLine = useMemo(
    () =>
      (preview.items || []).find(
        (i) => i.category === "service" && i.subcategory === "delivery"
      ),
    [preview.items]
  );
  const setupLine = useMemo(
    () =>
      (preview.items || []).find(
        (i) => i.category === "service" && i.subcategory === "setup"
      ),
    [preview.items]
  );

  const logisticsChips = useMemo(() => {
    const chips = [];
    if (state.fulfillment?.method === "delivery") {
      chips.push(
        `Delivery • ${Number(
          state.fulfillment?.address?.distanceKm || 0
        ).toFixed(1)} km`
      );
    } else {
      chips.push("Pickup");
      if (state.fulfillment?.pickupLocation) {
        chips.push(state.fulfillment.pickupLocation);
      }
    }
    if (state.fulfillment?.setupSelected) chips.push("Setup");
    if (state.fulfillment?.address?.line1)
      chips.push(state.fulfillment.address.line1);
    return chips;
  }, [state.fulfillment]);

  const handleAddToCart = async () => {
    if (!canSubmit) return;
    setError("");
    setBusy(true);
    try {
      const payload = buildPayload(state, { preview: false });
      const { data } = await api.post("/weddings/cart-items", payload);

      if (!data?.ok) {
        setError(
          data?.error?.message || "Failed to add to cart. Please try again."
        );
        setBusy(false);
        return;
      }
      const lines = Array.isArray(data?.items) ? data.items : [];
      if (!lines.length) {
        setError("Nothing to add. Please review your selections.");
        setBusy(false);
        return;
      }

      for (const line of lines) {
        // eslint-disable-next-line no-await-in-loop
        await addToCart(line);
      }
      setShowCartPreview(true);
    } catch (e) {
      console.error("weddings/cart-items failed", e);
      setError("Failed to add to cart. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  // Composite helper from config (legacy label/servings chips if present)
  const comp = config?.weddings?.composites?.[state.cake?.compositeKey] || null;

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
        Review your wedding order
      </Typography>
      <ReviewValidationBanner issues={clientIssues} />
      <ReviewServerWarnings warnings={preview.warnings} />
      <ReviewEventDetails
        state={state}
        addressConfirmed={addressConfirmed}
        onSubmit={handleAddToCart}
        canSubmit={canSubmit && !busy}
        busy={busy}
      />
      <ReviewCakeSummary
        hasCake={hasCake}
        comp={comp}
        cakeLine={cakeLine}
        state={state}
        total={cakeSectionTotal}
      />
      <ReviewDessertsSummary
        hasDess={hasDess}
        dessertLines={dessertLines}
        selections={state.desserts?.selections || []}
        total={dessertsSectionTotal}
      />
      <ReviewRentalsSummary
        hasRentals={hasRentals}
        rentalLines={rentalLines}
        depositLines={depositLines}
        total={rentalsSectionTotal}
        depositsTotal={depositsSectionTotal}
      />
      <ReviewLogisticsSummary
        logisticsChips={logisticsChips}
        deliveryLine={deliveryLine}
        setupLine={setupLine}
        deliveryFee={deliveryFeeAmt}
        setupFee={setupFeeAmt}
      />

      <ReviewCustomerSummary customer={state.customer} />

      <ReviewPricePreview
        busy={preview.busy}
        error={preview.error}
        totals={preview.totals}
        errorDetails={preview.errorDetails}
      />
      {preview.totals && <PaymentPlanBox previewTotals={preview.totals} />}
      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}
      <SharedCartPreview
        show={showCartPreview}
        setShowCartPreview={setShowCartPreview}
      />
    </Box>
  );
}
